import { PresetFilter } from '@/types/filterBuilder';

export const patientPresets: PresetFilter[] = [
  // Consent-Related Presets
  {
    id: 'consented',
    label: 'Consented Patients',
    description: 'Patients who have given consent',
    category: 'consent',
    icon: 'CheckCircle',
    filters: { consent_status: 'CONSENTED' },
  },
  {
    id: 'declined',
    label: 'Declined Consent',
    description: 'Patients who declined consent',
    category: 'consent',
    icon: 'XCircle',
    filters: { consent_status: 'DECLINED' },
  },
  {
    id: 'declined-date',
    label: 'Declined in Date Range',
    description: 'Patients who declined consent within a date range',
    category: 'consent',
    icon: 'Calendar',
    filters: { consent_status: 'DECLINED' },
    configurableFields: [
      {
        key: 'consent_date',
        label: 'Date Range',
        inputType: 'date-range',
        rangeKeys: { start: 'consent_date_start', end: 'consent_date_end' },
      },
    ],
  },
  {
    id: 'consented-date',
    label: 'Consented in Date Range',
    description: 'Patients who consented within a date range',
    category: 'consent',
    icon: 'Calendar',
    filters: { consent_status: 'CONSENTED' },
    configurableFields: [
      {
        key: 'consent_date',
        label: 'Date Range',
        inputType: 'date-range',
        rangeKeys: { start: 'consent_date_start', end: 'consent_date_end' },
      },
    ],
  },
  {
    id: 'answered-unclear',
    label: 'Answered but Consent Unclear',
    description: 'Patients who answered calls but consent status is uncertain or not asked',
    category: 'consent',
    icon: 'HelpCircle',
    filters: {
      consent_status: 'NOT_ASKED,UNCERTAIN',
      has_calls: true,
      call_statuses: 'completed',
    },
  },

  // Care Plan-Related Presets
  {
    id: 'consented-approved',
    label: 'Consented + Approved Care Plan',
    description: 'Consented patients with approved care plans',
    category: 'care-plan',
    icon: 'FileCheck',
    filters: {
      consent_status: 'CONSENTED',
      care_plan_status: 'approved',
    },
  },
  {
    id: 'consented-draft',
    label: 'Consented + Draft Care Plan',
    description: 'Consented patients with draft care plans waiting for approval',
    category: 'care-plan',
    icon: 'FileEdit',
    filters: {
      consent_status: 'CONSENTED',
      care_plan_status: 'draft',
    },
  },

  // Check-in Related Presets
  {
    id: 'completed-checkin',
    label: 'Completed Check-in',
    description: 'Patients who completed a check-in call',
    category: 'check-in',
    icon: 'CheckSquare',
    filters: {
      call_types: 'check_in',
      call_outcome_values: '{"is_complete": true}',
    },
    configurableFields: [
      {
        key: 'call_date',
        label: 'Date Range',
        inputType: 'date-range',
        rangeKeys: { start: 'call_date_start', end: 'call_date_end' },
      },
    ],
  },
  {
    id: 'pending-checkin',
    label: 'Pending Check-in',
    description: 'Patients with pending check-in calls',
    category: 'check-in',
    icon: 'Clock',
    filters: {
      call_types: 'check_in',
      call_statuses: 'pending',
    },
  },

  // Call-Related Presets
  {
    id: 'by-call-status',
    label: 'By Call Status',
    description: 'Filter patients by their latest call status',
    category: 'calls',
    icon: 'Phone',
    filters: {},
    configurableFields: [
      {
        key: 'latest_call_status',
        label: 'Call Status',
        inputType: 'multi-select',
        options: [
          { value: 'pending', label: 'Pending' },
          { value: 'completed', label: 'Completed' },
          { value: 'failed', label: 'Failed' },
          { value: 'voicemail', label: 'Voicemail' },
        ],
        required: true,
      },
    ],
  },
  {
    id: 'by-call-type',
    label: 'By Call Type',
    description: 'Filter patients by call type',
    category: 'calls',
    icon: 'PhoneCall',
    filters: {},
    configurableFields: [
      {
        key: 'call_types',
        label: 'Call Type',
        inputType: 'multi-select',
        options: [
          { value: 'consent', label: 'Consent' },
          { value: 'check_in', label: 'Check-in' },
        ],
        required: true,
      },
    ],
  },
  {
    id: 'calls-in-range',
    label: 'Calls in Date Range',
    description: 'Patients with calls in a specific date range',
    category: 'calls',
    icon: 'Calendar',
    filters: { has_calls: true },
    configurableFields: [
      {
        key: 'call_date',
        label: 'Date Range',
        inputType: 'date-range',
        rangeKeys: { start: 'call_date_start', end: 'call_date_end' },
        required: true,
      },
    ],
  },
  {
    id: 'has-calls',
    label: 'Has Calls',
    description: 'Patients who have at least one call',
    category: 'calls',
    icon: 'Phone',
    filters: { has_calls: true },
  },
  {
    id: 'no-calls',
    label: 'No Calls',
    description: 'Patients who have never been called',
    category: 'calls',
    icon: 'PhoneOff',
    filters: { has_calls: false },
  },
  {
    id: 'never-answered',
    label: 'Never Answered',
    description: 'Patients who have calls but never answered',
    category: 'calls',
    icon: 'PhoneMissed',
    filters: { never_answered: true },
  },
  {
    id: 'completed-calls',
    label: 'Completed Calls',
    description: 'Patients with completed calls (should_repeat: false)',
    category: 'calls',
    icon: 'CheckCircle2',
    filters: {
      call_outcome_values: '{"should_repeat": false}',
    },
  },
];

// Get presets by category
export function getPresetsByCategory(category: PresetFilter['category']): PresetFilter[] {
  return patientPresets.filter((p) => p.category === category);
}

// Get all preset categories with their presets
export function getPresetCategories(): { id: string; title: string; presets: PresetFilter[] }[] {
  return [
    { id: 'consent', title: 'Consent', presets: getPresetsByCategory('consent') },
    { id: 'care-plan', title: 'Care Plans', presets: getPresetsByCategory('care-plan') },
    { id: 'check-in', title: 'Check-ins', presets: getPresetsByCategory('check-in') },
    { id: 'calls', title: 'Calls', presets: getPresetsByCategory('calls') },
  ];
}
